import { DURATION, SUCCEEDED, UPLOADER_TYPE } from "constants/common";
import configUrls from "constants/configUrls";
import WidgetContainerContext from "contexts/WidgetContainer";
import { ApiResponseTypes } from "models/Common";
import { WidgetDetailTypes } from "models/Widget";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getRequest } from "services/api";
import utils from "services/utils";
import {
	convertURL,
	handlePendoTracking,
	insertCustomizedFont,
	replaceWidgetURL,
} from "services/widgetBuilder";
import { getClientDetailEnd, getClientDetailRequest, storeData } from "stores/actions";
import { StyledWrapper } from "styled-components/Common/CommonStyled";
import { opacity } from "styled-system";
import DynamicWidget from "../DynamicWidget";
import SkeletonFrame from "../WidgetBuilder/SkeletonFrame";
import WidgetBuilder from "../WidgetBuilder/WidgetBuilder";
import WidgetStatus from "../WidgetBuilder/WidgetStatus";

type WidgetContainerProps = {
	[key: string]: any;
};

const WidgetContainer = (props: WidgetContainerProps) => {
	const { globalProps } = props;
	const dispatch = useDispatch();
	const pathLocation = useLocation();
	const { search } = pathLocation;
	const href = convertURL(pathLocation).toLowerCase();
	const windowHref = window.location.href;
	const { clientDetail, getClientDetailStatus, storedCode } = useSelector(
		(state: any) => state.widgets,
	);
	const code = utils.getSearchValue(search, "code");

	const [widgetInfo, setWidgetInfo] = useState<WidgetDetailTypes>();
	const [authorizeWindow, setAuthorizeWindow] = useState<any>(null);
	const [isFailed, setIsFailed] = useState<boolean>(); // Default value is undefined
	const [opacity, setOpacity] = useState<number>(0);

	const getClientDetail = (uploaderLink: string) => {
		dispatch(getClientDetailRequest({ uploaderLink }));
	};

	// Is used for TikTok authorization
	const handlePostMessage = () => {
		if (window && window.opener) {
			window.opener?.postMessage(
				{
					passedParameter: search.substring(1),
				},
				"*",
			);
		}
	};

	useEffect(() => {
		handlePostMessage();
		if ((code && !clientDetail) || !code) {
			getClientDetail(href);
		}
	}, []);

	useEffect(() => {
		if (clientDetail && widgetInfo) {
			handlePendoTracking(clientDetail, widgetInfo);
			dispatch(
				storeData({
					isHiddenGeneralLoading: false,
				}),
			);
		}
	}, [clientDetail, widgetInfo]);

	useEffect(() => {
		if (widgetInfo) {
			const { metadata } = widgetInfo;
			const { colors } = metadata || {};

			if (colors?.enableCustomFontFamily) {
				insertCustomizedFont(colors?.fileFontUrls);
			}
		}
	}, [widgetInfo, widgetInfo?.metadata?.colors?.fileFontUrls, code]);

	useEffect(() => {
		if (getClientDetailStatus === SUCCEEDED) {
			if (clientDetail) {
				getMetaDataWidget(href, clientDetail.id);
				dispatch(getClientDetailEnd());
			} else {
				setIsFailed(true);
			}
		}
	}, [clientDetail, getClientDetailStatus]);

	useEffect(() => {
		if (authorizeWindow && storedCode) {
			authorizeWindow.close();
		}
	}, [authorizeWindow, storedCode]);

	useEffect(() => {
		window.addEventListener("message", (event) => {
			if (event?.data?.passedParameter) {
				const { passedParameter } = event.data;
				const uploadSource = passedParameter.includes("scopes=user.info.basic")
					? UPLOADER_TYPE.TIKTOK
					: UPLOADER_TYPE.INSTAGRAM;

				if (authorizeWindow) {
					authorizeWindow.close();
				}
				const href = utils.removeQueryStringParameter(
					windowHref,
					uploadSource === UPLOADER_TYPE.INSTAGRAM ? ["code", "scopes", "state"] : ["code"],
				);
				const updatedUrl = replaceWidgetURL(href, passedParameter);
				const params = new URLSearchParams(new URL(updatedUrl).search);
				if (!storedCode) {
					dispatch(
						storeData({
							storedCode: utils.getSearchValue(params, "code"),
							uploadSourceType: updatedUrl.includes("scopes=user.info.basic")
								? UPLOADER_TYPE.TIKTOK
								: UPLOADER_TYPE.INSTAGRAM,
						}),
					);
				}
				return;
			}
		});
	}, []);

	const getMetaDataWidget = (widgetLink: string, clientId: number) => {
		const url = `${configUrls.API.widgetBuilder}?widgetUrl=${widgetLink}&clientId=${clientId}`;

		const successCallback = (resp: ApiResponseTypes) => {
			if (resp) {
				const { data } = resp;
				setWidgetInfo({ ...data.result, metadata: JSON.parse(data.result.metadata) });
			}
			setIsFailed(false);

			setTimeout(() => {
				setOpacity(1);
			}, 0);
		};
		const failedCallback = () => {
			setIsFailed(true);
		};
		getRequest(
			url,
			{},
			undefined,
			undefined,
			undefined,
			undefined,
			successCallback,
			failedCallback,
		);
	};

	const contextValues = {
		authorizeWindow,
		setAuthorizeWindow,
		globalProps,
	};

	return (
		<WidgetContainerContext.Provider value={contextValues}>
			<StyledWrapper>
				{isFailed === undefined ? (
					<SkeletonFrame />
				) : (
					<StyledWrapper
						className="fade-out"
						style={{
							opacity,
						}}>
						{!widgetInfo?.metadata?.isV2 ? (
							<WidgetBuilder widgetInfo={widgetInfo} globalProps={globalProps} />
						) : (
							<DynamicWidget widgetDetails={widgetInfo} />
						)}
					</StyledWrapper>
				)}
			</StyledWrapper>

			{widgetInfo && <WidgetStatus widgetInfo={widgetInfo} isMobile={utils.isMobileWidth()} />}
		</WidgetContainerContext.Provider>
	);
};

export default WidgetContainer;
